import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import type { VendorUpdateSuggestion } from '../../mocks/data/vendor-update-suggestions'
import { SUPPLY_PERSONAS, useSupplyPersonaStore } from '../../store/role'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { fetchMe } from '../../lib/auth'
import { toast } from '../../lib/toast'
import { useT } from '../../i18n'
import { SUPPLY_VENDOR_UPDATES_STRINGS } from '../../i18n/supply/vendor-updates'

type FormState = {
  product_id: string
  product_name_snapshot: string
  field_name: string
  old_value: string
  new_value: string
  vendor_reason: string
}

const EMPTY: FormState = {
  product_id: '',
  product_name_snapshot: '',
  field_name: 'price_usd',
  old_value: '',
  new_value: '',
  vendor_reason: '',
}

export function SubmitNewUpdate() {
  const [form, setForm] = useState<FormState>(EMPTY)
  const qc = useQueryClient()
  const navigate = useNavigate()
  const persona = useSupplyPersonaStore((s) => s.supplyPersona)
  const info = SUPPLY_PERSONAS[persona]
  const t = useT(SUPPLY_VENDOR_UPDATES_STRINGS).submitNewUpdate

  const realMe = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    enabled: USE_REAL_API,
    staleTime: 5 * 60 * 1000,
  })
  const effectiveOrgType = USE_REAL_API
    ? (realMe.data?.active_membership?.org_type ?? 'SO3')
    : info.org_type
  const displayName = USE_REAL_API
    ? (realMe.data?.active_membership?.persona_label ??
        realMe.data?.user.display_name ??
        info.org_name)
    : info.org_name

  const submitMut = useMutation({
    mutationFn: async (body: FormState) => {
      const res = await fetch(apiUrl(`/supply/vendor-update-suggestions?persona=${persona}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const problem = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }))
        throw new Error(problem.detail ?? `HTTP ${res.status}`)
      }
      return res.json() as Promise<VendorUpdateSuggestion>
    },
    onSuccess: (suggestion) => {
      qc.invalidateQueries({ queryKey: ['supply', 'my-vendor-update-suggestions'] })
      qc.invalidateQueries({ queryKey: ['admin', 'vendor-update-suggestions'] })
      qc.invalidateQueries({ queryKey: ['admin', 'activity'] })
      qc.invalidateQueries({ queryKey: ['admin', 'metrics'] })
      toast.success(t.toasts.successTitle, {
        description: t.toasts.successDescFormat(suggestion.product_name_snapshot),
        audience: 'supply',
      })
      toast.info(t.toasts.adminTitle, {
        description: t.toasts.adminDescFormat(
          suggestion.vendor_org_name,
          suggestion.product_name_snapshot,
        ),
        audience: 'admin',
      })
      navigate({ to: '/supply/my-updates' })
    },
    onError: (err) => {
      toast.error(t.toasts.errorTitle, {
        description: err instanceof Error ? err.message : 'unknown',
        audience: 'supply',
      })
    },
  })

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const required = form.product_id && form.product_name_snapshot && form.field_name && form.new_value

  if (effectiveOrgType !== 'SO3') {
    return (
      <div className="max-w-2xl rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-6 text-sm text-[var(--color-text-muted)]">
        {t.nonSo3Gate.prefix}
        <span className="font-medium">{t.nonSo3Gate.so3Strong}</span>{' '}
        {t.nonSo3Gate.personaActiveLabel} {displayName} ({effectiveOrgType}).
        {!USE_REAL_API && <> {t.nonSo3Gate.hint}</>}
      </div>
    )
  }

  return (
    <div className="max-w-2xl">
      <div className="text-sm">
        <Link to="/supply/my-updates" className="text-[var(--color-info)] hover:underline">
          {t.backLink}
        </Link>
      </div>
      <h1 className="mt-3 text-2xl font-semibold text-[var(--color-text)]">{t.pageTitle}</h1>
      <p className="mt-2 text-[var(--color-text-muted)]">
        {t.description.prefix}
        <span className="font-mono">{t.description.approvedToken}</span>
        {t.description.orMid}
        <span className="font-mono">{t.description.rejectedToken}</span>
        {t.description.suffix}
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (required) submitMut.mutate(form)
        }}
        className="mt-6 space-y-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-5"
      >
        <div className="grid grid-cols-2 gap-4">
          <Field
            label={t.fields.productName.label}
            required
            requiredMark={t.requiredMark}
          >
            <input
              value={form.product_name_snapshot}
              onChange={(e) => update('product_name_snapshot', e.target.value)}
              placeholder={t.fields.productName.placeholder}
              className="w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus-visible:shadow-[var(--shadow-focus)]"
            />
          </Field>
          <Field
            label={t.fields.productId.label}
            required
            requiredMark={t.requiredMark}
          >
            <input
              value={form.product_id}
              onChange={(e) => update('product_id', e.target.value)}
              placeholder={t.fields.productId.placeholder}
              className="w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-2 text-sm font-mono text-[var(--color-text)] focus-visible:shadow-[var(--shadow-focus)]"
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Field
            label={t.fields.fieldName.label}
            required
            requiredMark={t.requiredMark}
          >
            <input
              value={form.field_name}
              onChange={(e) => update('field_name', e.target.value)}
              placeholder={t.fields.fieldName.placeholder}
              className="w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-2 text-sm font-mono text-[var(--color-text)] focus-visible:shadow-[var(--shadow-focus)]"
            />
          </Field>
          <Field label={t.fields.oldValue.label} requiredMark={t.requiredMark}>
            <input
              value={form.old_value}
              onChange={(e) => update('old_value', e.target.value)}
              placeholder={t.fields.oldValue.placeholder}
              className="w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus-visible:shadow-[var(--shadow-focus)]"
            />
          </Field>
          <Field
            label={t.fields.newValue.label}
            required
            requiredMark={t.requiredMark}
          >
            <input
              value={form.new_value}
              onChange={(e) => update('new_value', e.target.value)}
              placeholder={t.fields.newValue.placeholder}
              className="w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus-visible:shadow-[var(--shadow-focus)]"
            />
          </Field>
        </div>

        <Field label={t.fields.reason.label} requiredMark={t.requiredMark}>
          <textarea
            value={form.vendor_reason}
            onChange={(e) => update('vendor_reason', e.target.value)}
            rows={3}
            placeholder={t.fields.reason.placeholder}
            className="w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus-visible:shadow-[var(--shadow-focus)]"
          />
        </Field>

        {submitMut.isError && (
          <div className="text-sm text-[var(--color-danger)]" role="alert">
            {t.errorPrefix}{' '}
            {submitMut.error instanceof Error ? submitMut.error.message : 'unknown'}
          </div>
        )}

        <div className="flex items-center gap-2 pt-2">
          <button
            type="submit"
            disabled={submitMut.isPending || !required}
            className="rounded-md bg-[var(--color-action)] px-4 py-2 text-sm font-medium text-[var(--color-action-foreground)] hover:bg-[var(--color-action-hover)] disabled:opacity-50"
          >
            {submitMut.isPending ? t.actions.submitting : t.actions.submit}
          </button>
          <button
            type="button"
            onClick={() => setForm(EMPTY)}
            className="rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-4 py-2 text-sm text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]"
          >
            {t.actions.clear}
          </button>
        </div>
      </form>
    </div>
  )
}

function Field({
  label,
  required,
  requiredMark,
  children,
}: {
  label: string
  required?: boolean
  requiredMark: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-[var(--color-text)]">
        {label}
        {required && <span className="ml-1 text-[var(--color-danger)]">{requiredMark}</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  )
}
